import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
    /** Totale duur van 1 loop (milliseconden) */
    durationMs?: number;

    /** Duur van de “reveal” (blur->scherp + mist weg) in milliseconden */
    revealMs?: number;

    /** Pauzeer animatie */
    paused?: boolean;

    /** SVG breedte/hoogte (px). Laat leeg voor 100% responsive. */
    width?: number;
    height?: number;

    /** Start-blur sterkte (SVG stdDeviation) */
    blurStart?: number;

    /** Kleur van de gouden tekst (</>) */
    goldColor?: string;

    /** Kleur van schema strokes + </> (lichtgrijs) */
    baseColor?: string;

    /** Extra glow sterkte (0.6–1.2 is normaal) */
    glowBlur?: number;

    /** Hoe lang (ms) de </> goud blijft na de reveal */
    goldHoldMs?: number;
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}
function clamp01(n: number) {
    return clamp(n, 0, 1);
}
function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
}

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const onChange = () => setReduced(!!mq.matches);
        onChange();
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return reduced;
}

/**
 * StructureScene (looping)
 * - Reveal: schema wazig -> scherp + mist trekt weg
 * - GEEN pulse over lijnen
 * - Wel </> tekst die goud “aan gaat” na de reveal (eventueel met glow)
 */
export default function StructureScene({
    durationMs = 6000,
    revealMs = 2200,
    paused = false,

    width,
    height,

    blurStart = 4.5,
    goldColor = "rgb(var(--primary))",
    baseColor = "#8A8A8A",
    glowBlur = 0.35,
    goldHoldMs = 3400,
}: Props) {
    const reduced = usePrefersReducedMotion();

    const ids = useMemo(() => {
        const s = Math.random().toString(16).slice(2);
        return {
            blur: `blur-${s}`,
            fogBlur: `fogBlur-${s}`,
            mask: `mask-${s}`,
            glow: `glow-${s}`,
        };
    }, []);

    const rafRef = useRef<number | null>(null);
    const startRef = useRef<number | null>(null);
    const [tMs, setTMs] = useState(0);

    const safeDuration = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 9000;
    const safeReveal = clamp(revealMs ?? 2200, 0, safeDuration);
    const safeGoldHold = clamp(goldHoldMs ?? 1400, 0, safeDuration);

    useEffect(() => {
        if (reduced) {
            setTMs(safeDuration);
            return;
        }

        const tick = (now: number) => {
            if (startRef.current === null) startRef.current = now;

            if (!paused) {
                const elapsed = now - startRef.current;
                setTMs(elapsed % safeDuration);
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            startRef.current = null;
        };
    }, [paused, safeDuration, reduced]);

    const revealP = safeReveal === 0 ? 1 : clamp01(tMs / safeReveal);

    // Visual mapping
    const blurStdDev = reduced ? 0 : lerp(blurStart, 0, revealP);
    const schemaOpacity = reduced ? 1 : lerp(0.6, 1.0, revealP);

    const fogOpacity = reduced ? 0 : lerp(0.95, 0.0, revealP);
    const revealScale = reduced ? 1.15 : lerp(0.6, 1.15, revealP);

    // Gold toggles on after reveal, stays for goldHoldMs, then off until next loop
    const goldOn =
        reduced ||
        (tMs >= safeReveal && tMs < safeReveal + safeGoldHold);

    const svgSizing =
        typeof width === "number" && typeof height === "number"
            ? { width, height }
            : { width: "100%", height: "100%" as const };

    return (
        <svg
            viewBox="0 0 100.54166 42.333334"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block" }}
            role="img"
            aria-label="Structuur: mist naar scherpte met gouden </> zonder lijn-pulse"
            {...svgSizing}
        >
            <defs>
                {/* Blur for schema */}
                <filter id={ids.blur} x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation={blurStdDev} />
                </filter>

                {/* Soft blur for fog */}
                <filter id={ids.fogBlur} x="-35%" y="-35%" width="170%" height="170%">
                    <feGaussianBlur stdDeviation="2.2" />
                </filter>

                {/* Glow for gold text */}
                <filter id={ids.glow} x="-160%" y="-160%" width="420%" height="420%">
                    <feGaussianBlur stdDeviation={glowBlur} result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                {/* Mask: white = visible, black = hidden */}
                <mask id={ids.mask}>
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    <g style={{ opacity: fogOpacity }}>
                        <rect x="0" y="0" width="100%" height="100%" fill="black" />
                        <g
                            filter={`url(#${ids.fogBlur})`}
                            transform={`scale(${revealScale}) translate(${(1 - revealScale) * 50}, ${(1 - revealScale) * 20})`}
                        >
                            <circle cx="25" cy="18" r="18" fill="white" />
                            <circle cx="55" cy="26" r="20" fill="white" />
                            <circle cx="82" cy="20" r="16" fill="white" />
                        </g>
                    </g>
                </mask>

                <style>{`
          .schema { filter: url(#${ids.blur}); }

          .flow-base {
            fill: none;
            stroke: ${baseColor};
            stroke-width: 0.79375;
            stroke-linecap: round;
            stroke-linejoin: miter;
            stroke-miterlimit: 4;
            stroke-dasharray: none;
            stroke-opacity: 1;
            vector-effect: non-scaling-stroke;
          }

          .code-grey { fill: ${baseColor}; }
          .code-gold { fill: ${goldColor}; filter: url(#${ids.glow}); }
        `}</style>
            </defs>

            <g mask={`url(#${ids.mask})`}>
                <g className="schema" style={{ opacity: schemaOpacity }}>
                    <g
                        id="g1322"
                        style={{ strokeWidth: 1, strokeMiterlimit: 4, strokeDasharray: "none" }}
                        transform="translate(-28.793551,-14.165048)"
                    >
                        {/* Boxes */}
                        <rect
                            style={{
                                fill: "none",
                                stroke: baseColor,
                                strokeWidth: 1,
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeMiterlimit: 4,
                                strokeDasharray: "none",
                                paintOrder: "fill markers stroke",
                            }}
                            width="26.458334"
                            height="13.229167"
                            x="65.665375"
                            y="15.883387"
                            ry="2.6458333"
                        />
                        <rect
                            style={{
                                fill: "none",
                                stroke: baseColor,
                                strokeWidth: 1,
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeMiterlimit: 4,
                                strokeDasharray: "none",
                                paintOrder: "fill markers stroke",
                            }}
                            width="26.458334"
                            height="13.229167"
                            x="29.29355"
                            y="41.550877"
                            ry="2.6458333"
                        />
                        <rect
                            style={{
                                fill: "none",
                                stroke: baseColor,
                                strokeWidth: 1,
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeMiterlimit: 4,
                                strokeDasharray: "none",
                                paintOrder: "fill markers stroke",
                            }}
                            width="26.458334"
                            height="13.229167"
                            x="65.83522"
                            y="41.550877"
                            ry="2.6458333"
                        />
                        <rect
                            style={{
                                fill: "none",
                                stroke: baseColor,
                                strokeWidth: 1,
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeMiterlimit: 4,
                                strokeDasharray: "none",
                                paintOrder: "fill markers stroke",
                            }}
                            width="26.458334"
                            height="13.229167"
                            x="102.37688"
                            y="41.550877"
                            ry="2.6458333"
                        />

                        {/* </> (3 paths) — grijs -> goud (na reveal) */}
                        <path
                            className={goldOn ? "code-gold" : "code-grey"}
                            d="m 76.730584,20.737738 q 0,0.04522 -0.06459,0.122732 -0.400494,0.503846 -1.285455,1.098127 -0.826825,0.561982 -1.492161,0.820365 -0.07106,0.01938 -0.155029,0.09043 0.0323,0.04522 0.14211,0.09689 0.678255,0.316519 1.466323,0.859123 0.872042,0.60074 1.285454,1.098126 0.08398,0.103354 0.08398,0.15503 0,0.0065 -0.193787,0.381115 -0.193788,0.374655 -0.226085,0.44571 -0.02584,0.07105 -0.0646,0.07105 -0.03876,0 -0.142111,-0.09043 -1.175642,-0.981854 -1.569675,-1.272535 -0.794528,-0.581361 -1.737625,-1.027072 -0.14857,-0.07105 -0.14857,-0.226085 v -0.884961 q 0,-0.161489 0.174408,-0.226085 0.387575,-0.14211 1.627812,-0.949557 0.329438,-0.219625 0.859123,-0.671795 0.594281,-0.497387 0.846204,-0.678255 0.09043,-0.07106 0.14211,-0.07106 0.103354,0 0.45217,0.859123 z"
                        />
                        <path
                            className={goldOn ? "code-gold" : "code-grey"}
                            d="m 80.567569,18.018259 q 0,0.0323 -0.02584,0.155029 -0.406953,1.530919 -1.188561,4.592755 -0.251923,0.994774 -0.74931,3.068296 -0.103353,0.497387 -0.348817,1.466322 -0.04522,0.129192 -0.109813,0.129192 -0.852663,-0.342357 -0.910799,-0.381115 -0.02584,-0.01292 -0.02584,-0.0646 0,-0.06459 0.03876,-0.187327 0.439251,-1.51154 1.227318,-4.734865 0.335898,-1.459863 1.05937,-4.36667 0.03876,-0.129191 0.103353,-0.129191 0.04522,0 0.484468,0.200246 0.44571,0.193787 0.44571,0.251924 z"
                        />
                        <path
                            className={goldOn ? "code-gold" : "code-grey"}
                            d="m 85.160324,23.360323 q 0,0.15503 -0.14857,0.226085 -1.65365,0.807447 -3.3073,2.299607 -0.03876,0.04522 -0.142111,0.09043 -0.484468,-0.839744 -0.484468,-0.89788 0,-0.29068 1.253157,-1.149803 1.098127,-0.75577 1.582595,-0.962476 0.07106,-0.02584 0.14211,-0.09689 -0.03876,-0.03876 -0.155029,-0.09043 -1.892655,-0.813906 -2.777616,-1.918492 -0.06459,-0.07751 -0.06459,-0.122732 0,-0.02584 0.187327,-0.374655 0.187328,-0.355277 0.206706,-0.426332 0.02584,-0.07106 0.05168,-0.07106 0.0323,0 0.14857,0.08397 0.549064,0.471549 1.705327,1.35005 0.678255,0.439251 0.800986,0.516766 0.523226,0.316519 0.826825,0.432791 0.174409,0.0646 0.174409,0.226085 z"
                        />

                        {/* Verbindingslijnen: ALLEEN lichtgrijs, GEEN pulse overlay */}
                        <path className="flow-base" d="M 78.820937,29.204968 V 41.494531" />
                        <path className="flow-base" d="M 55.611391,48.275918 H 65.809113" />
                        <path className="flow-base" d="M 92.262182,48.188758 H 102.4599" />
                    </g>
                </g>
            </g>
        </svg>
    );
}
